import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Reveal } from "./Reveal";
import { fetchChampions } from "../lib/api";
import { getRankStyle } from "../lib/rankStyle";
import { ChampionAvatar } from "./ChampionAvatar";
import { ChampionModal } from "./ChampionModal";

// Co-chairs first, then everyone else alphabetically by first name.
const CO_CHAIRS = [{ name: "Ashleigh Russell" }, { name: "Deirdre Fasano" }];
const MEMBERS = [
  { name: "Rylie Erb" },
  { name: "Terri Fisher" },
  { name: "Noel Holly" },
  { name: "Angelika Judd" },
  { name: "Puja Romero" },
  { name: "Amanda Vaughan" },
  { name: "Melissa Weiman" },
];

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z ]/g, "").replace(/\s+/g, " ").trim();
// First + last token key, so a champion carrying a middle name/suffix still matches.
const nameKey = (s) => {
  const p = norm(s).split(" ").filter(Boolean);
  return p.length > 1 ? `${p[0]} ${p[p.length - 1]}` : p[0] || "";
};
const initialsOf = (name) => {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[p.length - 1]?.[0] || "")).toUpperCase();
};

const MemberCard = ({ member, champ, role, hero, onOpen }) => {
  const person = { name: member.name, photo: champ?.photo || null, initials: initialsOf(member.name) };
  const clickable = Boolean(champ);
  return (
    <button
      type="button"
      onClick={() => clickable && onOpen(champ)}
      disabled={!clickable}
      className={`flex w-28 flex-col items-center text-center focus:outline-none sm:w-36 ${clickable ? "cursor-pointer" : "cursor-default"}`}
      data-testid={`committee-member-${norm(member.name).replace(/ /g, "-")}`}
    >
      <ChampionAvatar
        champion={person}
        ringStyle={getRankStyle(null).ringStyle}
        sizeClass={hero ? "h-28 w-28 sm:h-32 sm:w-32" : "h-24 w-24"}
        textClass={hero ? "text-3xl" : "text-2xl"}
      />
      <p className={`mt-4 font-display smallcaps leading-tight text-mds-ink ${hero ? "text-xl sm:text-2xl" : "text-lg"}`}>
        {member.name}
      </p>
      {role && (
        <p className="mt-1 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-mds-gold">{role}</p>
      )}
    </button>
  );
};

export const Committee = () => {
  const { data } = useQuery({ queryKey: ["champions"], queryFn: fetchChampions, staleTime: 0 });
  const [selected, setSelected] = useState(null);
  const champions = useMemo(() => data?.champions || [], [data]);
  const byName = useMemo(() => {
    const m = {};
    champions.forEach((c) => {
      m[norm(c.name)] = c;
      m[nameKey(c.name)] = c;
    });
    return m;
  }, [champions]);
  const findChamp = (name) => byName[norm(name)] || byName[nameKey(name)] || null;

  return (
    <section id="committee" className="relative bg-mds-white" data-testid="committee-section">
      <div className="mx-auto max-w-[1200px] px-5 py-24 sm:px-8 sm:py-32">
        <Reveal className="text-center">
          <p className="mt-2 pb-6 pt-4 font-script text-6xl leading-[1.3] text-gold sm:text-8xl">Our Planning Committee</p>
          <p className="mx-auto mt-7 max-w-3xl text-balance font-body text-base leading-relaxed text-mds-inksoft">
            With heartfelt gratitude, we recognize our inaugural Planning Committee for the time, talent, and passion they
            have so generously shared. Their leadership has laid the foundation for what will become a cherished annual
            tradition of impact, generosity, and hope for many years to come.
          </p>
        </Reveal>

        {/* Co-chairs */}
        <Reveal delay={0.06}>
          <div className="mt-14 flex flex-wrap justify-center gap-x-16 gap-y-10 sm:gap-x-24">
            {CO_CHAIRS.map((m) => (
              <MemberCard key={m.name} member={m} champ={findChamp(m.name)} role="Co-Chair" hero onOpen={setSelected} />
            ))}
          </div>
        </Reveal>

        {/* Committee members */}
        <Reveal delay={0.1}>
          <div
            className="mx-auto mt-16 flex max-w-3xl flex-wrap justify-center gap-x-12 gap-y-12 sm:gap-x-16"
            data-testid="committee-members"
          >
            {MEMBERS.map((m) => (
              <MemberCard key={m.name} member={m} champ={findChamp(m.name)} onOpen={setSelected} />
            ))}
          </div>
        </Reveal>
      </div>

      <ChampionModal
        champion={selected ? champions.find((c) => c.account_id === selected.account_id) || selected : null}
        onClose={() => setSelected(null)}
        hideRank
      />
    </section>
  );
};
